<div class="flex flex-col gap-(--space-5) p-(--space-5)">
  <form [formGroup]="form">
    <div class="mb-(--gap-loose)">
      <label for="envelope-name" id="dialog-title" class="block text-sm font-medium mb-(--space-2)">{{
        'graphs.envelope.nameLabel' | transloco
      }}</label>
      <input
        pInputText
        id="envelope-name"
        formControlName="name"
        type="text"
        [maxlength]="NAME_MAX_LENGTH"
        [attr.aria-required]="true"
        [attr.aria-invalid]="nameControl().touched && nameControl().invalid ? 'true' : null"
        [class.ng-invalid]="nameControl().touched && nameControl().invalid"
        [class.ng-dirty]="nameControl().touched"
        class="w-full" />
    </div>

    <div class="mb-(--gap-loose)">
      <div class="flex items-center justify-end gap-(--gap-loose) mb-(--gap-relaxed)">
        <button
          type="button"
          (click)="copyPoints()"
          class="flex items-center gap-(--gap-tight) text-sm cursor-pointer text-(color:--text-secondary) hover:text-(color:--p-primary-text) transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(color:--p-primary-text)"
          [attr.aria-label]="'graphs.envelope.copyAllPoints' | transloco">
          <span class="material-symbols text-sm" aria-hidden="true">content_copy</span>
          <span>{{ 'graphs.envelope.copy' | transloco }}</span>
        </button>
        <button
          type="button"
          (click)="resetPoints()"
          class="flex items-center gap-(--gap-tight) text-sm cursor-pointer text-(color:--text-secondary) hover:text-(color:--p-primary-text) transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(color:--p-primary-text)"
          [attr.aria-label]="'graphs.envelope.resetAllPoints' | transloco">
          <span class="material-symbols text-sm" aria-hidden="true">refresh</span>
          <span>{{ 'graphs.envelope.reset' | transloco }}</span>
        </button>
      </div>
      <div
        class="flex items-center gap-(--gap-relaxed) mb-1 text-xs font-medium text-(color:--text-secondary) uppercase tracking-wide">
        <span class="w-8 text-center">#</span>
        <div class="flex-1 flex items-center gap-(--gap-relaxed)">
          <span class="flex-1">X</span>
          <span class="flex-1">Y</span>
        </div>
        <span class="w-10"></span>
      </div>

      <div formArrayName="points" class="flex flex-col max-h-[400px] overflow-y-auto" role="list">
        @for (point of pointsArray().controls; track point) {
          @if ($index > 0) {
            <div class="flex items-center gap-(--gap-relaxed) h-3 -my-1" role="separator">
              <span class="w-8"></span>
              <div class="group relative flex-1 flex items-center justify-center h-full z-10">
                <div
                  class="absolute inset-x-0 top-1/2 h-px bg-transparent group-hover:bg-[var(--border)] transition-colors duration-150"></div>
                <button
                  type="button"
                  (click)="insertPointAt($index)"
                  class="relative opacity-0 group-hover:opacity-100 focus-visible:opacity-100 flex items-center justify-center w-5 h-5 rounded-full bg-[var(--p-primary-color)] text-white shadow-sm cursor-pointer transition-all duration-150 hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(color:--p-primary-color)"
                  [attr.aria-label]="'graphs.envelope.insertPointAt' | transloco: { position: $index + 1 }">
                  <span class="material-symbols text-[9px]" aria-hidden="true">add</span>
                </button>
              </div>
              <span class="w-10"></span>
            </div>
          }
          <div
            [formGroupName]="$index"
            class="flex items-center gap-(--gap-relaxed) py-1.5 rounded transition-colors"
            [class.opacity-50]="isLastRow($index) && isEmptyRow($index)"
            [class.hover:bg-gray-50]="!isLastRow($index) || !isEmptyRow($index)"
            role="listitem">
            <span
              class="w-8 text-center text-xs select-none"
              style="font-variant-numeric: tabular-nums"
              [class.text-(color:--text-secondary)]="!isEmptyRow($index)"
              [class.text-gray-300]="isEmptyRow($index)"
              >{{ $index + 1 }}</span
            >
            <div class="flex-1 flex items-center gap-(--gap-relaxed)">
              <div class="flex-1">
                <input
                  pInputText
                  formControlName="x"
                  type="number"
                  step="0.01"
                  [min]="MIN_VALUE"
                  [max]="MAX_VALUE"
                  (paste)="onPaste($event, $index, 'x')"
                  [placeholder]="'graphs.envelope.valueX' | transloco"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    pointsArray().at($index).get('x')?.touched && pointsArray().at($index).get('x')?.invalid ? 'true' : null
                  "
                  [attr.aria-label]="getAriaLabelX($index)"
                  [class.ng-invalid]="pointsArray().at($index).get('x')?.touched && pointsArray().at($index).get('x')?.invalid"
                  [class.ng-dirty]="pointsArray().at($index).get('x')?.touched"
                  class="w-full" />
              </div>
              <div class="flex-1">
                <input
                  pInputText
                  formControlName="y"
                  type="number"
                  step="0.01"
                  [min]="MIN_VALUE"
                  [max]="MAX_VALUE"
                  (paste)="onPaste($event, $index, 'y')"
                  [placeholder]="'graphs.envelope.valueY' | transloco"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="
                    pointsArray().at($index).get('y')?.touched && pointsArray().at($index).get('y')?.invalid ? 'true' : null
                  "
                  [attr.aria-label]="getAriaLabelY($index)"
                  [class.ng-invalid]="pointsArray().at($index).get('y')?.touched && pointsArray().at($index).get('y')?.invalid"
                  [class.ng-dirty]="pointsArray().at($index).get('y')?.touched"
                  class="w-full" />
              </div>
            </div>
            @if (!(isLastRow($index) && isEmptyRow($index))) {
              <button
                type="button"
                (click)="removeOrResetPoint($index)"
                class="flex items-center justify-center w-10 h-10 text-(color:--text-secondary) hover:text-red-500 cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
                [attr.aria-label]="getPointActionAriaLabel($index)"
                [title]="
                  pointsArray().length <= MIN_POINTS ? ('graphs.envelope.reset' | transloco) : ('graphs.envelope.delete' | transloco)
                ">
                <span class="material-symbols text-lg" aria-hidden="true">delete</span>
              </button>
            } @else {
              <span class="w-10"></span>
            }
          </div>
        }
      </div>
    </div>

    <ces-dialog-footer [submitDisabled]="!isValid()" (onCancel)="cancel()" (onSubmit)="save()" />
  </form>
</div>
